import React, { createContext, useContext, useReducer, useEffect } from 'react';
import { User, AuthContextType } from '../types/auth';
import authService from '../services/authService';

// Estados do contexto
interface AuthState {
  user: User | null;
  isLoading: boolean;
  error: string | null;
}

// Ações do reducer
type AuthAction =
  | { type: 'AUTH_START' }
  | { type: 'AUTH_SUCCESS'; payload: User }
  | { type: 'AUTH_ERROR'; payload: string }
  | { type: 'AUTH_LOGOUT' }
  | { type: 'CLEAR_ERROR' };

// Reducer para gerenciar estado de autenticação
function authReducer(state: AuthState, action: AuthAction): AuthState {
  console.log('🔄 AUTHREDUCER: Action disparada:', action.type, action.type === 'AUTH_SUCCESS' ? `(${action.payload?.email})` : '');
  
  switch (action.type) {
    case 'AUTH_START':
      console.log('⏳ AUTHREDUCER: Iniciando autenticação...');
      return {
        ...state,
        isLoading: true,
        error: null
      };
    
    case 'AUTH_SUCCESS':
      console.log('✅ AUTHREDUCER: Autenticação bem-sucedida:', {
        email: action.payload.email,
        role: action.payload.role,
        roles: action.payload.roles
      });
      return {
        ...state,
        user: action.payload,
        isLoading: false,
        error: null
      };
    
    case 'AUTH_ERROR':
      console.log('❌ AUTHREDUCER: Erro de autenticação:', action.payload);
      return {
        ...state,
        user: null,
        isLoading: false,
        error: action.payload
      };
    
    case 'AUTH_LOGOUT':
      console.log('🚪 AUTHREDUCER: Logout/sem usuário');
      return {
        ...state,
        user: null,
        isLoading: false,
        error: null
      };
    
    case 'CLEAR_ERROR':
      console.log('🧹 AUTHREDUCER: Limpando erro');
      return {
        ...state,
        error: null
      };
    
    default:
      console.log('⚠️ AUTHREDUCER: Action desconhecida:', action);
      return state;
  }
}

// Estado inicial - comece com loading=true para evitar flash de conteúdo não autenticado
const initialState: AuthState = {
  user: null,
  isLoading: true,  // Deve começar como true até que a verificação seja concluída
  error: null
};

// Criar contexto
const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Provider do contexto
export function AuthProvider({ children }: { children: React.ReactNode }) {
  console.log('🏁 AUTHPROVIDER: Component iniciando...');
  
  const [state, dispatch] = useReducer(authReducer, initialState);
  const initializationRef = React.useRef<'pending' | 'running' | 'completed'>('pending');

  console.log('📊 AUTHPROVIDER: Estado inicial:', { user: !!state.user, isLoading: state.isLoading, error: state.error });

  // Check session on initialization
  useEffect(() => {
    console.log('⚡ AUTHPROVIDER: useEffect executando...');
    
    // Evitar múltiplas execuções
    if (initializationRef.current !== 'pending') {
      console.log('🔄 AuthContext: Init já executado, status:', initializationRef.current);
      return;
    }

    const initAuth = async () => {
      console.log('🔄 AuthContext: Iniciando verificação de autenticação...');
      initializationRef.current = 'running';
      
      try {
        // Try to get current user from Azure AD or local session
        console.log('🔍 AuthContext: Chamando authService.getCurrentUser()...');
        const user = await authService.getCurrentUser();
        
        console.log('📊 AuthContext: Resultado getCurrentUser:', {
          hasUser: !!user,
          email: user?.email,
          role: user?.role,
          roles: user?.roles,
          permissions: user?.permissions
        });
        
        if (user) {
          console.log('✅ AuthContext: Usuário autenticado encontrado, disparando AUTH_SUCCESS');
          dispatch({ type: 'AUTH_SUCCESS', payload: user });
        } else {
          console.log('❌ AuthContext: Nenhum usuário autenticado, disparando AUTH_LOGOUT');
          dispatch({ type: 'AUTH_LOGOUT' });
        }
      } catch (error) {
        console.error('❌ AuthContext: Falha na verificação de autenticação:', error);
        dispatch({ type: 'AUTH_LOGOUT' });
      } finally {
        initializationRef.current = 'completed';
        console.log('✅ AuthContext: Inicialização completa, status:', initializationRef.current);
      }
    };

    initAuth();
  }, []); // Empty dependency array - run once on mount

  // Proactive token refresh: renova o cookie antes de expirar (a cada 4 minutos)
  // Também renova quando a aba volta a ficar visível (mobile: tela desbloqueada)
  useEffect(() => {
    if (!state.user) return;

    const doRefresh = async () => {
      try {
        console.log('🔄 Proactive refresh: verificando token...');
        await authService.refreshSessionCookie();
      } catch (err) {
        console.warn('⚠️ Proactive token refresh failed:', err);
      }
    };

    // Periodic refresh every 4 minutes
    const intervalId = setInterval(doRefresh, 4 * 60 * 1000);

    // Refresh when page becomes visible again (important for mobile)
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        console.log('🔄 Página voltou a ficar visível — renovando token proativamente');
        doRefresh();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Refresh on focus (backup for visibility API issues on some mobile browsers)
    const handleFocus = () => {
      console.log('🔄 Janela recebeu foco — renovando token proativamente');
      doRefresh();
    };
    window.addEventListener('focus', handleFocus);

    return () => {
      clearInterval(intervalId);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
    };
  }, [state.user]);

  // Main login function (Azure AD only)
  const login = async (): Promise<void> => {
    // If user is already authenticated, don't login again
    if (state.user) {
      return;
    }
    
    dispatch({ type: 'AUTH_START' });
    
    try {
      // Try Azure AD login
      const user = await authService.login();
      dispatch({ type: 'AUTH_SUCCESS', payload: user });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Login error';
      dispatch({ type: 'AUTH_ERROR', payload: message });
      throw error;
    }
  };

  // Login com Google (Fase 8): recebe o ID token do botão do Google Identity Services.
  const loginWithGoogle = async (idToken: string): Promise<void> => {
    if (state.user) {
      return;
    }

    dispatch({ type: 'AUTH_START' });

    try {
      const user = await authService.loginWithGoogle(idToken);
      dispatch({ type: 'AUTH_SUCCESS', payload: user });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Falha no login com Google';
      dispatch({ type: 'AUTH_ERROR', payload: message });
      throw error;
    }
  };

  // Check authentication status (useful for refreshing after operations)
  const checkAuthStatus = async (): Promise<void> => {
    dispatch({ type: 'AUTH_START' });
    
    try {
      const user = await authService.checkAuthenticationStatus();
      if (user) {
        dispatch({ type: 'AUTH_SUCCESS', payload: user });
      } else {
        dispatch({ type: 'AUTH_LOGOUT' });
      }
    } catch (error) {
      console.error('❌ Erro ao verificar status de autenticação:', error);
      dispatch({ type: 'AUTH_LOGOUT' });
    }
  };


  // Logout function
  const logout = async (): Promise<void> => {
    dispatch({ type: 'AUTH_START' });
    
    try {
      await authService.logout();
      dispatch({ type: 'AUTH_LOGOUT' });
    } catch (error) {
      // Even with error, clear local session
      dispatch({ type: 'AUTH_LOGOUT' });
      console.error('Logout error:', error);
    }
  };

  // Clear error
  const clearError = (): void => {
    dispatch({ type: 'CLEAR_ERROR' });
  };

  const value: AuthContextType = {
    user: state.user,
    login,
    loginWithGoogle,
    logout,
    checkAuthStatus,
    isLoading: state.isLoading,
    error: state.error,
    clearError
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

// Hook para usar o contexto
// eslint-disable-next-line react-refresh/only-export-components
export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  
  if (context === undefined) {
    throw new Error('useAuth deve ser usado dentro de um AuthProvider');
  }
  
  return context;
}

